import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { PropTypes } from 'prop-types';
import { formatRoute } from 'react-router-named-routes';
import { EntryType } from '../../API';
import RecipesSearchMealResultCard from './recipes_search_meal_result_card';
import AddToPlannerModal from '../meal/add_to_planner_modal';
import { copyMealToPlanner } from '../../operations/planner_operations';
import { END_USER_RECIPES_BOARD_ROUTE } from '../../services/routes';
import { mealFromSharedMeal } from '../../services/meals';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import {
  useCreateMealMutation,
  useGetGRCRecipesByIDsQuery,
  useGetSpaceQuery,
  useUpdateDbPlannerWeekOptimisticallyMutation,
  useUpdateMealMutation,
  useLazyGetFullMealQuery,
  useLazyGetFullSharedMealQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import { useTracker } from '../../operations/tracking_operations';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import { userIsCreator } from '../../services/users';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetDistilledMealByIDAndParentQuery,
  useGetDistilledSharedMealByIDAndParentQuery,
  useGetUserProfileQuery,
  useLazyGetOrCreateDbPlannerWeekByStartDateQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';

const RecipesSearchResultItem = ({
  entryType,
  objectID,
  parentID,
  title,
  onClick,
}) => {
  const dispatch = useDispatch();

  const tracker = useTracker();

  const [createMeal] = useCreateMealMutation();

  const [updateMeal] = useUpdateMealMutation();

  const [updateDbPlannerWeek] = useUpdateDbPlannerWeekOptimisticallyMutation();

  const { data: userProfile } = useGetUserProfileQuery();

  const { data: distilledMealInMeals } = useGetDistilledMealByIDAndParentQuery(
    { id: objectID, parentID },
    { skip: !objectID || !parentID },
  );

  const { data: distilledMealInSharedMeals } =
    useGetDistilledSharedMealByIDAndParentQuery(
      { id: objectID, parentID },
      {
        skip: !objectID || !parentID,
      },
    );

  const isSharedMeal = !!distilledMealInSharedMeals;

  const { data: grcRecipes } = useGetGRCRecipesByIDsQuery(
    [objectID],
    /* GRC recipes don't have a parent */ { skip: !objectID || !!parentID },
  );

  const grcRecipe = (grcRecipes || {})[objectID];

  const distilledRecipeObj =
    entryType === EntryType.MEAL
      ? (distilledMealInMeals?.recipes ||
          distilledMealInSharedMeals?.recipes ||
          [])[0]
      : grcRecipe?.recipe;

  const isSmorgStudio = userIsCreator(userProfile);

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery(
    undefined,
    {
      skip: !userProfile || isSmorgStudio,
    },
  );

  const { data: healthProSpace } = useGetSpaceQuery();

  const currentSpaceID = isSmorgStudio
    ? healthProSpace?.id
    : currentSpaceMembership?.spaceID;

  const customerFoodDataID = customerFoodDataIDOfSpace(currentSpaceID);

  const [addToPlannerAnchorEl, setAddToPlannerAnchorEl] = useState(null);

  const onClickAddToPlanner = (target) => {
    setAddToPlannerAnchorEl(target);
  };

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [lazyGetSharedMeal] = useLazyGetFullSharedMealQuery();

  const afterAddingToFavourites = useCallback(
    ({ favouritesRecipesBoardID }) => {
      dispatch(
        showGlobalSnackbar({
          notificationText: 'Meal was added to your favourites',
          linkText: 'View',
          linkTarget: formatRoute(END_USER_RECIPES_BOARD_ROUTE, {
            recipesBoardId: favouritesRecipesBoardID,
          }),
        }),
      );
    },
    [dispatch],
  );

  const [lazyGetOrCreateDbPlannerWeekByStartDate] =
    useLazyGetOrCreateDbPlannerWeekByStartDateQuery();

  const onCopyToPlanner = useCallback(
    async (plannerViewWeekStartDate, dayIndexes, scaleToServings) => {
      let fullMealToCopy;
      if (isSharedMeal) {
        const { data: fullSharedMeal } = await lazyGetSharedMeal(objectID);
        fullMealToCopy = mealFromSharedMeal(fullSharedMeal);
      } else {
        const { data: fullMeal } = await lazyGetMeal(objectID);
        fullMealToCopy = fullMeal;
      }
      copyMealToPlanner(
        fullMealToCopy,
        plannerViewWeekStartDate,
        dayIndexes,
        customerFoodDataID,
        tracker,
        userProfile,
        currentSpaceMembership,
        lazyGetOrCreateDbPlannerWeekByStartDate,
        createMeal,
        updateMeal,
        updateDbPlannerWeek,
        lazyGetMeal,
        scaleToServings,
      );
    },
    [
      isSharedMeal,
      customerFoodDataID,
      tracker,
      userProfile,
      currentSpaceMembership,
      lazyGetOrCreateDbPlannerWeekByStartDate,
      createMeal,
      updateMeal,
      updateDbPlannerWeek,
      lazyGetSharedMeal,
      objectID,
      lazyGetMeal,
    ],
  );

  return (
    <>
      <RecipesSearchMealResultCard
        mealID={objectID}
        parentID={parentID}
        title={title}
        imageUrl={distilledRecipeObj?.imageUrl}
        favouriteEnabled={isSharedMeal}
        onAddToPlanner={onClickAddToPlanner}
        afterAddingToFavourites={afterAddingToFavourites}
        onClick={onClick}
      />
      {addToPlannerAnchorEl && (
        <AddToPlannerModal
          anchorEl={addToPlannerAnchorEl}
          copyToPlannerEnabled
          editServingsEnabled
          onCopyToPlanner={(
            plannerViewWeekStartDate,
            dayIndexes,
            scaleToServings,
          ) => {
            onCopyToPlanner(
              plannerViewWeekStartDate,
              dayIndexes,
              scaleToServings,
            );
            setAddToPlannerAnchorEl(null);
            dispatch(
              showGlobalSnackbar({
                notificationText: 'The meal has been copied to your planner',
                linkText: 'View planner',
                linkTarget: `/planner/${plannerViewWeekStartDate}`,
              }),
            );
          }}
          onDismiss={() => setAddToPlannerAnchorEl(null)}
        />
      )}
    </>
  );
};

RecipesSearchResultItem.propTypes = {
  entryType: PropTypes.string.isRequired,
  objectID: PropTypes.string.isRequired,
  parentID: PropTypes.string,
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

RecipesSearchResultItem.defaultProps = {
  parentID: null,
};

export default RecipesSearchResultItem;
